import { rejectUnsafeChosenRoomName } from '@/lib/calls/chosenRoomName';
import type { SupabaseClient } from '@supabase/supabase-js';

interface GetOrCreateCallIdOptions {
  userId?: string | null;
  roomName?: string;
  startTime?: string;
  // Org scope stamped at call creation. Calls born WITHOUT org_unit_id are
  // unusable by non-platform-admins: the canonical actions guard returns
  // 409 'Call organization scope missing' on every /api/call-actions request
  // → the live tips panel never hydrates (the rk.davis no-tips incident,
  // 2026-06-11). A NULL user_id likewise breaks RecordingAuth ownership
  // (the video no_owner_record 403).
  orgUnitId?: string | null;
}

function isConflictError(error: { code?: string; status?: number } | null | undefined): boolean {
  if (!error) return false;
  return error.code === '23505' || error.code === 'PGRST409' || error.status === 409;
}

/**
 * Create a call record if it doesn't exist, handling concurrent inserts safely.
 *
 * Also BACKFILLS org_unit_id / user_id onto an existing row that lacks them:
 * other writers (e.g. persistCallPhoneAccess) upsert call rows with no org/user
 * context, and a row that stays org-less is permanently 409-gated for
 * non-platform-admins. The first authenticated owner-session touch heals it
 * (callers of this function are already behind requireAuth + room-ownership
 * gates, so the stamped identity is the call's de-facto owner).
 */
export async function getOrCreateCallId(
  supabase: SupabaseClient,
  callId: string,
  options: GetOrCreateCallIdOptions = {}
): Promise<string | null> {
  const normalizedCallId = callId.trim();
  if (!normalizedCallId) return null;

  const { data: existingCall, error: selectError } = await supabase
    .from('calls')
    .select('id, org_unit_id, user_id')
    .eq('call_id', normalizedCallId)
    .maybeSingle();

  if (selectError && selectError.code !== 'PGRST116') {
    throw selectError;
  }

  if (existingCall?.id) {
    const backfill: Record<string, string> = {};
    if (!existingCall.org_unit_id && options.orgUnitId) {
      backfill.org_unit_id = options.orgUnitId;
    }
    if (!existingCall.user_id && options.userId) {
      backfill.user_id = options.userId;
    }
    if (Object.keys(backfill).length > 0) {
      // Guarded UPDATE: only fill columns that are still NULL (the .is filters
      // make concurrent heals idempotent and never overwrite a real value).
      let update = supabase.from('calls').update(backfill).eq('id', existingCall.id);
      if (backfill.org_unit_id) update = update.is('org_unit_id', null);
      if (backfill.user_id) update = update.is('user_id', null);
      const { error: backfillError } = await update;
      if (backfillError) {
        // Non-fatal: the row exists and is usable by admins; the next touch retries.
        console.warn('[getOrCreateCallId] org/user backfill failed', backfillError);
      }
    }
    return existingCall.id;
  }

  rejectUnsafeChosenRoomName(callId, 'getOrCreateCallId');
  rejectUnsafeChosenRoomName(options.roomName ?? callId, 'getOrCreateCallId');

  const payload = {
    call_id: normalizedCallId,
    room_name: options.roomName ?? normalizedCallId,
    start_time: options.startTime ?? new Date().toISOString(),
    user_id: options.userId ?? null,
    // The DB trigger derives the trusted ui-frontend build at call start.
    org_unit_id: options.orgUnitId ?? null,
  };

  const { data: createdCalls, error: upsertError } = await supabase
    .from('calls')
    .upsert(payload, {
      onConflict: 'call_id',
      ignoreDuplicates: true,
    })
    .select('id');

  if (upsertError && !isConflictError(upsertError)) {
    throw upsertError;
  }

  if (createdCalls && createdCalls.length > 0 && createdCalls[0]?.id) {
    return createdCalls[0].id;
  }

  const { data: fallbackCall, error: fallbackError } = await supabase
    .from('calls')
    .select('id')
    .eq('call_id', normalizedCallId)
    .maybeSingle();

  if (fallbackError && fallbackError.code !== 'PGRST116') {
    throw fallbackError;
  }

  return fallbackCall?.id ?? null;
}
